import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, AlertTriangle, CheckCircle, Clock, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getFlagStyle = (type) => {
  switch (type) {
    case 'ready': return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    case 'process_needed': return 'bg-amber-100 text-amber-800 border-amber-300';
    case 'critical_forge': return 'bg-red-100 text-red-800 border-red-300';
    case 'critical_cut': return 'bg-red-100 text-red-800 border-red-300';
    case 'rm_failure': return 'bg-red-200 text-red-900 border-red-400 font-bold';
    default: return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

export default function WeeklyTarget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1);
    return d.toISOString().split('T')[0];
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/weekly-dispatch-target?start_date=${weekStart}`);
      setData(res.data);
    } catch (err) { console.error(err); toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/export/weekly-target-pdf?start_date=${weekStart}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url; link.download = `Weekly_Target_${weekStart}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) { console.error(err); toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="weekly-target-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="weekly-target-title">WEEKLY DISPATCH TARGET</h1>
          <p className="text-sm text-text-muted mt-2">Material availability against dispatch plan with action flags</p>
        </div>
        <div className="flex items-center gap-3">
          <Input type="date" value={weekStart} onChange={e => setWeekStart(e.target.value)} className="w-40 rounded-sm" data-testid="week-start-input" />
          <Button onClick={fetchData} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />REFRESH
          </Button>
          <Button onClick={handleExportPdf} disabled={exporting || !data?.targets?.length}
            className="bg-green-600 text-white hover:bg-green-700 rounded-sm" data-testid="export-pdf-btn">
            <FileDown className="w-4 h-4 mr-2" />{exporting ? 'EXPORTING...' : 'PDF'}
          </Button>
        </div>
      </div>

      {data?.summary && (
        <div className="grid grid-cols-5 gap-3">
          <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-sm text-center">
            <div className="text-2xl font-bold text-emerald-700">{data.summary.ready}</div>
            <div className="text-xs text-emerald-600 uppercase font-medium">FG Ready</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 p-3 rounded-sm text-center">
            <div className="text-2xl font-bold text-amber-700">{data.summary.process_needed}</div>
            <div className="text-xs text-amber-600 uppercase font-medium">Process Needed</div>
          </div>
          <div className="bg-red-50 border border-red-200 p-3 rounded-sm text-center">
            <div className="text-2xl font-bold text-red-700">{data.summary.critical_forge}</div>
            <div className="text-xs text-red-600 uppercase font-medium">Forge Now</div>
          </div>
          <div className="bg-red-50 border border-red-200 p-3 rounded-sm text-center">
            <div className="text-2xl font-bold text-red-700">{data.summary.critical_cut}</div>
            <div className="text-xs text-red-600 uppercase font-medium">Cut Now</div>
          </div>
          <div className="bg-red-100 border border-red-300 p-3 rounded-sm text-center">
            <div className="text-2xl font-bold text-red-800">{data.summary.rm_failure}</div>
            <div className="text-xs text-red-700 uppercase font-medium">RM Required</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-border p-12 text-center text-text-muted">Loading...</div>
      ) : !data?.targets?.length ? (
        <div className="bg-white border border-border p-12 text-center text-text-muted">
          No dispatch plans for the selected week ({data?.week_start} to {data?.week_end}).
        </div>
      ) : (
        <div className="space-y-4">
          {data.targets.map((t, idx) => (
            <div key={`${t.part_id}-${idx}`} className="bg-white border border-border rounded-sm overflow-hidden" data-testid={`target-card-${idx}`}>
              <div className="flex items-center justify-between p-4 border-b border-border bg-gray-50/50">
                <div>
                  <span className="font-mono font-bold text-sm">{t.part_id}</span>
                  <span className="text-text-muted text-xs ml-2">{t.part_name}</span>
                  <span className="text-text-muted text-xs ml-3">({t.steel_grade} {t.section})</span>
                </div>
                <div className="flex items-center gap-2">
                  {t.flags.map((f, fi) => (
                    <span key={fi} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getFlagStyle(f.type)}`}>
                      {f.type === 'ready' ? <CheckCircle className="w-3 h-3 mr-1" /> :
                       f.type === 'rm_failure' ? <AlertTriangle className="w-3 h-3 mr-1" /> :
                       <Clock className="w-3 h-3 mr-1" />}
                      {f.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-4 gap-4 mb-3">
                  <div>
                    <div className="text-xs text-text-muted uppercase font-medium">Target</div>
                    <div className="text-lg font-bold font-mono">{t.total_planned}</div>
                  </div>
                  <div>
                    <div className="text-xs text-text-muted uppercase font-medium">Total Available</div>
                    <div className={`text-lg font-bold font-mono ${t.total_available >= t.total_planned ? 'text-emerald-600' : 'text-red-600'}`}>{t.total_available}</div>
                  </div>
                  <div>
                    <div className="text-xs text-text-muted uppercase font-medium">Pending After Cascade</div>
                    <div className={`text-lg font-bold font-mono ${t.pending_after_cascade > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid={`pending-${idx}`}>{t.pending_after_cascade}</div>
                  </div>
                  <div>
                    <div className="text-xs text-text-muted uppercase font-medium">Vendor WIP (Total)</div>
                    <div className="text-lg font-bold font-mono">{t.vendor_wip}</div>
                  </div>
                </div>
                <div className="flex gap-2 mb-3 flex-wrap">
                  {['FG', 'HT', 'Machining', 'Forging', 'Cutting'].map(stg => {
                    const internalQty = t.stage_stock?.[stg] || 0;
                    const vendorQty = t.vendor_wip_by_stage?.[stg] || 0;
                    const combined = internalQty + vendorQty;
                    return (
                      <div key={stg} className={`px-2 py-1 text-xs font-mono rounded-sm border ${combined > 0 ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-400'}`} data-testid={`stage-cell-${idx}-${stg}`}>
                        <div className="font-semibold">{stg}: {combined}</div>
                        {combined > 0 && (
                          <div className="text-[10px] text-gray-500">int={internalQty} · wip={vendorQty}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {t.coverage && t.coverage.length > 0 && (
                  <div className="mt-3 mb-2 bg-slate-50 border border-slate-200 rounded-sm p-2">
                    <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold mb-1">Cascade Trail</div>
                    <div className="flex flex-wrap gap-2">
                      {t.coverage.map((c, ci) => (
                        <div key={ci} className={`text-xs px-2 py-0.5 rounded-sm border ${c.covered_target ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`} data-testid={`coverage-${idx}-${ci}`}>
                          {c.stage}: used {c.used} of {c.available}{c.covered_target ? ' ✓' : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="space-y-1">
                  {t.suggestions.map((s, si) => (
                    <div key={si} className="flex items-start gap-2 text-xs">
                      <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"></span>
                      <span className="text-gray-700">{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
